import {
  useVirtualizer,
  useWindowVirtualizer,
  type Virtualizer,
} from "@tanstack/react-virtual";
import { forwardRef, type JSX, type ReactNode, useEffect, useRef } from "react";

interface CommonVirtualizerProps {
  children: ReactNode[];
  canFetchNextPage: boolean;
  fetchNextPage: () => void;
  //component rendered at the end of the list
  endComponent: JSX.Element;
}

export function WindowScrollInfiniteVirtualizer({
  children,
  canFetchNextPage,
  estimatedItemSize,
  endComponent,
  ...rest
}: CommonVirtualizerProps & { estimatedItemSize: number }) {
  const virtualizer = useWindowVirtualizer({
    count: endComponent ? children.length + 1 : children.length,
    estimateSize: () => estimatedItemSize,
    overscan: 5,
  });

  return (
    <InfiniteVirtualizer
      virtualizer={virtualizer}
      canFetchNextPage={canFetchNextPage}
      endComponent={endComponent}
      {...rest}
    >
      {children}
    </InfiniteVirtualizer>
  );
}
export function CustomScrollInfiniteVirtualizer({
  children,
  canFetchNextPage,
  estimatedItemSize,
  endComponent,
  ...rest
}: CommonVirtualizerProps & { estimatedItemSize: number }) {
  const containerRef = useRef<HTMLDivElement>();
  const virtualizer = useVirtualizer({
    count: endComponent ? children.length + 1 : children.length,
    getScrollElement: () => containerRef.current?.parentElement,
    estimateSize: () => estimatedItemSize,
    overscan: 5,
  });
  return (
    <InfiniteVirtualizer
      ref={containerRef}
      virtualizer={virtualizer}
      canFetchNextPage={canFetchNextPage}
      endComponent={endComponent}
      {...rest}
    >
      {children}
    </InfiniteVirtualizer>
  );
}
const InfiniteVirtualizer = forwardRef<
  HTMLDivElement,
  CommonVirtualizerProps & {
    virtualizer:
      | Virtualizer<HTMLElement, Element>
      | Virtualizer<Window, Element>;
  }
>(
  (
    { fetchNextPage, canFetchNextPage, children, endComponent, virtualizer },
    ref,
  ) => {
    useEffect(() => {
      const [lastItem] = [...virtualizer.getVirtualItems()].reverse();

      if (!lastItem) {
        return;
      }

      if (lastItem.index >= children.length / 1.25 && canFetchNextPage) {
        fetchNextPage();
      }
    }, [
      canFetchNextPage,
      fetchNextPage,
      children.length,
      virtualizer.getVirtualItems(),
    ]);

    return (
      <div
        ref={ref}
        className="w-full relative"
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const isEndRow = virtualRow.index > children.length - 1;

          return (
            <div
              key={virtualRow.index}
              className="absolute top-0 left-0 w-full"
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {isEndRow ? endComponent : children[virtualRow.index]}
            </div>
          );
        })}
      </div>
    );
  },
);
